/**
 * DirectoryView
 *
 * Scrollable fish encyclopedia screen. Shows every species in
 * FISH_LIBRARY order grouped by tier. Undiscovered fish appear
 * silhouetted with a mystery name until caught.
 *
 * Props:
 *   directory  {object}   - The player's directory state from useDirectory.
 *   onBack     {function} - Navigates back to the menu.
 */

import { useState } from 'react';
import { FISH_LIBRARY } from '../constants/fishLibrary';
import { rarityGlows } from '../constants/effects';
import DirectoryDetailCard from './DirectoryDetailCard';

const TIER_LABELS = {
  LOW:  'LOW TIER',
  MID:  'MID TIER',
  HIGH: 'HIGH TIER',
  MYTHICAL:  'MYTHICAL TIER',
};

export default function DirectoryView({ directory, onBack }) {
  const [selectedFish, setSelectedFish] = useState(null);

  const discoveredCount = Object.keys(directory).length;
  const totalCount      = FISH_LIBRARY.length;

  // Build the tier-divided list in a single pass through FISH_LIBRARY.
  // Each time the tier changes from the previous entry, insert a divider.
  const rows = [];
  let lastTier = null;

  for (const fish of FISH_LIBRARY) {
    if (fish.tier !== lastTier) {
      rows.push({ type: 'divider', tier: fish.tier });
      lastTier = fish.tier;
    }
    rows.push({ type: 'fish', fish });
  }

  const topRarityOf = (fish) => fish.rarities[fish.rarities.length - 1];

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      minHeight: '100vh', width: '100%', backgroundColor: '#05070a',
      color: 'white', padding: '20px', boxSizing: 'border-box', overflowY: 'auto',
    }}>

      <h2 style={{textAlign: 'center', color: '#f1c40f', marginBottom: '8px', letterSpacing: '2px' }}>
        FISH DIRECTORY
      </h2>

      {/* Discovery progress counter */}
      <p style={{ color: '#8b949e', fontSize: '9px', marginBottom: '24px', letterSpacing: '1px' }}>
        {discoveredCount} / {totalCount} DISCOVERED
      </p>

      {/* Detail card overlay */}
      {selectedFish && (
        <DirectoryDetailCard
          fish={selectedFish}
          entry={directory[selectedFish.id]}
          onClose={() => setSelectedFish(null)}
        />
      )}

      {/* Tier-divided grid */}
      <div style={{ width: '100%', maxWidth: '500px' }}>
        {rows.map((row, index) => {

          // --- Tier Divider ---
          if (row.type === 'divider') {
            return (
              <div
                key={`divider-${row.tier}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  margin: '20px 0 12px',
                }}
              >
                <div style={{ flex: 1, height: '1px', backgroundColor: '#30363d' }} />
                <span style={{ color: '#8b949e', fontSize: '8px', letterSpacing: '2px', whiteSpace: 'nowrap' }}>
                  {TIER_LABELS[row.tier]}
                </span>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#30363d' }} />
              </div>
            );
          }

          // --- Fish Card ---
          const { fish } = row;
          const entry       = directory[fish.id];
          const discovered  = !!entry;
          const glowColor   = rarityGlows[topRarityOf(fish)] || '#30363d';

          return (
            <div
              key={fish.id}
              style={{ display: 'inline-block', width: 'calc(25% - 9px)', margin: '4px' }}
            >
              <div
                onClick={() => discovered && setSelectedFish(fish)}
                style={{
                  backgroundColor: '#161b22',
                  border: `2px solid ${discovered ? glowColor : '#30363d'}`,
                  borderRadius: '8px', padding: '8px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  cursor: discovered ? 'pointer' : 'default',
                  boxShadow: discovered ? `0 0 10px ${glowColor}40` : 'none',
                  position: 'relative',
                }}
              >
                <img
                  src={`assets/fish/${fish.id}.png`}
                  alt={discovered ? fish.name : '???'}
                  style={{
                    width: '100%', height: '40px', objectFit: 'contain',
                    imageRendering: 'pixelated',
                    // Silhouette undiscovered fish with a pure black filter
                    filter: discovered ? 'none' : 'brightness(0)',
                  }}
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=?'; }}
                />
                <span style={{
                  fontSize: '8px', marginTop: '6px', textAlign: 'center',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  width: '100%', textTransform: 'uppercase',
                  color: discovered ? '#ffffff' : '#30363d',
                }}>
                  {discovered ? fish.name : '???'}
                </span>

                {/* Variant dot indicator — shown only if any special variant seen */}
                {discovered && entry.variantsSeen.length > 0 && (
                  <span style={{
                    position: 'absolute', top: '4px', right: '4px',
                    width: '6px', height: '6px', borderRadius: '50%',
                    backgroundColor: '#f1c40f',
                  }} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={onBack}
        className="menu-btn"
        style={{
          marginTop: '30px', padding: '12px 24px', backgroundColor: '#21262d',
          color: '#c9d1d9', border: '1px solid #30363d', borderRadius: '6px',
          cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px',
        }}
      >
        BACK TO MENU
      </button>

    </div>
  );
}